<script lang="ts" setup>
import { NuxtLink } from '#components'
import type { MenuType } from '~/types/menu.type'
import ExBadge from '~/ui/Badge/ExBadge.vue'

interface IProps {
  isActive?: boolean
  item: MenuType
  isUnderItem?: boolean
}

const props = defineProps<IProps>()

const { changeShowModal } = useModal()

// isOpen의 초기값을 computed로 설정
const initialIsOpen = computed(() => {
  if (props.isActive) return true
  if (props.item.underItems) {
    return props.item.underItems.some(item => item.isActive)
  }
  return false
})

// ref 대신 useState를 사용하여 상태 유지
const isOpen = useState(`sidebar-item-${props.item.name}`, () => initialIsOpen.value)

const displayedFeatures = computed(() => {
  const parentFeatures = props.item.featured || []
  
  if (!props.item.underItems || isOpen.value) return parentFeatures
  
  const childFeatures = props.item.underItems.flatMap(child => child.featured || [])
  return [...new Set([...parentFeatures, ...childFeatures])]
})

const toggle = () => {
  isOpen.value = !isOpen.value
}

const handleClick = () => {
  if (props.item.to) return
  if (props.item.underItems) {
    toggle()
    return
  }
  changeShowModal()
}

const getComponentType = (path?: string) => {
  return path ? NuxtLink : 'button'
}
</script>

<template>
  <div
    v-if="isActive && !item.underItems"
    class="active-indicator"
  />
  <component
    :is="getComponentType(item.to)"
    class="menu-container group"
    :class="{active: props.isActive, '!pl-[3.25rem]': isUnderItem}"
    :to="item.to"
    @click="handleClick"
    :target="item.external ? '_blank' : undefined"
  >
    <FontAwesomeIcon
      v-if="item.icon"
      class="text-xl"
      :icon="item.icon"
    />
    <span class="whitespace-nowrap text-left">
      {{ item.name }}
    </span>
    <div class="flex items-center gap-x-1">
      <ExBadge
        v-for="featured in displayedFeatures"
        :key="featured"
        :label="featured"
        padding-x="4"
        padding-y="4"
        bg-color="point-color"
        bg-opacity="40"
        text-size="xs"
        text-color="point-color"
      />
    </div>
    <FontAwesomeIcon
      v-if="item.underItems"
      class="ml-auto text-xl"
      :icon="isOpen ? 'caret-up' : 'caret-down'"
    />
    <FontAwesomeIcon
      v-if="item.external"
      class="ml-auto text-xl"
      :icon="'external-link-square-alt'"
    />
  </component>
  <ul
    id="dropdown"
    :class="{hidden: !isOpen}"
  >
    <li
      v-for="underItem of item.underItems"
      :key="underItem.name"
      class="relative"
    >
      <SidebarItem
        :is-active="underItem.isActive"
        :item="underItem"
        is-under-item
      />
    </li>
  </ul>
</template>

<style scoped lang="scss">
.active-indicator {
  @apply absolute w-1 bg-explorer-blue top-0 left-0 h-full rounded-r-lg;
}

.menu-container {
  @apply flex gap-x-3 items-center w-full p-4 pl-6 text-primary text-lg transition duration-300 whitespace-nowrap no-underline leading-none text-opacity-40;

  &:hover {
    @apply text-opacity-100 ease-[ease];
  }

  &:focus {
    @apply text-opacity-100;
  }

  &.active {
    @apply bg-gradient-to-r from-explorer-blue/10 text-opacity-100;
  }
}
</style>
